<template>
  <div class="twcs-bg-white twcs-border-b twcs-border-t twcs-border-gray-200">
    <div
      tabindex="0"
      role="listbox"
      ref="listboxModal"
      aria-labelledby="slide-over-heading"
      :aria-multiselectable="panels[currentPanelIndex].multiselect"
      :aria-activedescendant="activeDescendant"
      @keydown.enter.prevent="onOptionSelect()"
      @keydown.space.prevent="onOptionSelect()"
      @keydown.arrow-down.prevent="onArrowDown()"
      @keydown.arrow-up.prevent="onArrowUp()"
      @keydown.arrow-left.stop.prevent="onArrowLeft()"
    >
      <div
        v-for="(category, categoryIndex) in Object.keys(options)"
        :key="category"
        @mouseenter="onMouseMoveCategory(category, categoryIndex)"
        @mouseleave="onMouseMoveCategory(null, null)"
        :id="`${panels[currentPanelIndex].id}-${categoryIndex}-modal`"
        class="twcs-relative"
      >
        <div :id="categoryIndex" class="twcs-z-[1000] twcs-sticky twcs-top-0 twcs-border-t twcs-border-b twcs-border-gray-200 twcs-bg-gray-50 twcs-px-6 twcs-py-1 twcs-text-base twcs-font-medium twcs-text-gray-500" role="presentation">
          <span v-if="category === '0'">{{ panels[currentPanelIndex].catchAllCategory }}</span>
          <span v-else>{{ category }}</span>
        </div>
        <ul class="twcs-relative twcs-z-0 twcs-divide-y twcs-divide-gray-200" role="group" :aria-label="category === '0' ? panels[currentPanelIndex].catchAllCategory : category">
          <li
            v-for="(option, index) in options[category]"
            :key="option.value"
            role="option"
            :id="`${panels[currentPanelIndex].id}-${categoryIndex}-modal-item-${index}`"
            @click="onOptionSelect()"
            @mouseenter="onMouseMoveOption(index)"
            @mouseleave="onMouseMoveOption(null)"
            :class="[selectedIndex === index && selectedCategory === category ? 'twcs-text-white twcs-bg-kctcs-navy-600' : 'twcs-text-gray-900', 'twcs-cursor-default twcs-select-none twcs-relative twcs-w-full twcs-py-2 twcs-pl-8 twcs-pr-4' ]"
            :aria-selected="panels[currentPanelIndex].select && selected[category][index]"
          >
            <!-- Selected: "font-semibold", Not Selected: "font-normal" -->
            <span
              v-if="panels[currentPanelIndex].select"
              :class="{ 'twcs-font-semibold': selected[category][index],
                        'twcs-font-normal': !selected[category][index] } "
              class="twcs-block twcs-truncate twcs-w-full"
            >
              {{ option.text }}
            </span>
            <span v-else class="twcs-block twcs-truncate twcs-w-full twcs-font-normal">
              {{ option.text }}
            </span>
            <!--
              Checkmark, only display for selected option.

              Highlighted: "text-white", Not Highlighted: "text-kctcs-navy-600"
            -->
            <span
              v-if="panels[currentPanelIndex].select && selected[category][index]"
              :class="[selectedIndex === index && selectedCategory === category ? 'twcs-text-white' : 'twcs-text-kctcs-navy-600', 'twcs-absolute twcs-inset-y-0 twcs-left-0 twcs-flex twcs-items-center twcs-pl-1.5']"
            >
              <!-- Heroicon name: check -->
              <svg class="twcs-h-5 twcs-w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { watch, ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import Bugsnag from '@bugsnag/js';
import { usePanelStateStore } from '@/stores/panel-state.js';

let props = defineProps({
	options: { type: Object },
	selected: { type: Object },
	hasFocus: { type: Boolean, default: false },
});

let emit = defineEmits(['optionSelect', 'panelBack']);

const panelState = usePanelStateStore();

const { panels, currentPanelIndex } = storeToRefs(panelState);
const { updatePanelState, updateCurrentPanel } = panelState;

let isMounted = ref(false);

let selectedCategory = ref(panels.value[currentPanelIndex.value].currentCategory);
let selectedCategoryIndex = ref(panels.value[currentPanelIndex.value].currentCategoryIndex);
let selectedIndex = ref(panels.value[currentPanelIndex.value].currentIndex); // hover/focused element
let listboxModal = ref(null); // ul element
let activeDescendant = ref(`${panels.value[currentPanelIndex.value].id}-${selectedCategoryIndex.value}-modal-item-${selectedIndex.value}`); // active active descendant of ul element

function setActiveDescendant() {
    activeDescendant.value = `${panels.value[currentPanelIndex.value].id}-${selectedCategoryIndex.value}-modal-item-${selectedIndex.value}`;
}

watch(() => props.hasFocus, (hasFocus) => {
	if(isMounted.value && hasFocus) {
		listboxModal.value.focus();
        setActiveDescendant();
        try {
            listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value === null ? 0 : selectedIndex.value].scrollIntoView({ block: 'nearest' });
        } catch (error) {
            Bugsnag.notify(error, (event) => {
                event.addMetadata('ModalFilterSelectByGroup:watchHasFocus', {
                    selectedCategory: selectedCategory.value,
                    selectedIndex: selectedIndex.value,
                    selectedCategoryIndex: selectedCategoryIndex.value,
                    activeDescendant: activeDescendant.value,
                    listboxModal: listboxModal.value,
                    listboxModalChildren: listboxModal.value.children,
                    options: props.options,
                    selected: props.selected,
                    hasFocus: props.hasFocus,
                    isMounted: isMounted.value,
                    panels: panels.value,
                });
            });
            console.log(error);
        }
	}
});

onMounted(() => {
	listboxModal.value.focus();
    setActiveDescendant();
    try {
        listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value === null ? 0 : selectedIndex.value].scrollIntoView({ block: 'nearest' });
    } catch (error) {
        Bugsnag.notify(error, (event) => {
            event.addMetadata('ModalFilterSelectByGroup:onMounted', {
                selectedCategory: selectedCategory.value,
                selectedIndex: selectedIndex.value,
                selectedCategoryIndex: selectedCategoryIndex.value,
                activeDescendant: activeDescendant.value,
                listboxModal: listboxModal.value,
                listboxModalChildren: listboxModal.value.children,
                options: props.options,
                selected: props.selected,
                hasFocus: props.hasFocus,
                isMounted: isMounted.value,
                panels: panels.value,
            });
        });
        console.log(error);
    }
});

function onArrowUp() {
	if(selectedCategory.value === null
		|| selectedCategoryIndex.value === null
		|| selectedIndex.value === null
		|| !props.options.hasOwnProperty(selectedCategory.value)) {
		selectedCategory.value = '0';
		selectedCategoryIndex.value = 0;
		selectedIndex.value = 0;
        setActiveDescendant();
        if (isMounted.value) {
            listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        }
		return;
	}
	if(selectedIndex.value === 0) {
		// get prev category, last option
		const categoriesArr = Object.keys(props.options);
		if(categoriesArr.indexOf(selectedCategory.value) === 0) {
			// if it's the first category
			selectedCategory.value = categoriesArr[categoriesArr.length - 1];
			selectedCategoryIndex.value = categoriesArr.length - 1;
			selectedIndex.value = props.options[selectedCategory.value].length - 1;
            setActiveDescendant();
            if (isMounted.value) {
			    listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            }
		} else {
			selectedCategory.value = categoriesArr[categoriesArr.indexOf(selectedCategory.value) - 1];
			selectedCategoryIndex.value = categoriesArr.indexOf(selectedCategory.value);
			selectedIndex.value = props.options[selectedCategory.value].length - 1;
            setActiveDescendant();
            if (isMounted.value) {
			    listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            }
			listboxModal.value.scrollTop -= 28;
		}
	} else {
		selectedIndex.value--;
        setActiveDescendant();
        if (isMounted.value) {
		    listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        }
		listboxModal.value.scrollTop -= 28;
	}
}

function onArrowDown() {
	if(selectedCategory.value === null
		|| selectedCategoryIndex.value === null
		|| selectedIndex.value === null
		|| !props.options.hasOwnProperty(selectedCategory.value)) {
		selectedCategory.value = '0';
		selectedCategoryIndex.value = 0;
		selectedIndex.value = 0;
        setActiveDescendant();
        if (isMounted.value) {
		    listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        }
		return;
	}
	if(selectedIndex.value === props.options[selectedCategory.value].length - 1) {
		// get next category, first option
		const categoriesArr = Object.keys(props.options);
		if(categoriesArr.indexOf(selectedCategory.value) === categoriesArr.length - 1) {
			// if it's the last category
			selectedCategory.value = categoriesArr[0];
			selectedCategoryIndex.value = 0;
			selectedIndex.value = 0;
            setActiveDescendant();
            if (isMounted.value) {
			    listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            }
			listboxModal.value.scrollTop -= 28;
		} else {
			selectedCategory.value = categoriesArr[categoriesArr.indexOf(selectedCategory.value) + 1];
			selectedCategoryIndex.value = categoriesArr.indexOf(selectedCategory.value);
			selectedIndex.value = 0;
            setActiveDescendant();
            if (isMounted.value) {
			    listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            }
		}
	} else {
		selectedIndex.value++;
        setActiveDescendant();
        if (isMounted.value) {
		    listboxModal.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        }
	}
}

function onArrowLeft() {
    updatePanelState({
        currentIndex: selectedIndex.value,
        currentCategory: selectedCategory.value,
        currentCategoryIndex: selectedCategoryIndex.value,
    });

    updateCurrentPanel(panels.value[currentPanelIndex.value].parentPanelId);
    emit('panelBack');
}

function onOptionSelect() {
    updatePanelState({
        currentIndex: selectedIndex.value,
        currentCategory: selectedCategory.value,
		currentCategoryIndex: selectedCategoryIndex.value,
    });
    emit('optionSelect', {
        index: selectedIndex.value,
        category: selectedCategory.value,
    });
}

function onMouseMoveOption(index) {
	selectedIndex.value = index;
}

function onMouseMoveCategory(category, index) {
	selectedCategory.value = category;
	selectedCategoryIndex.value = index;
}
</script>
